import type { AddableStep, AddedItemDto } from '@bk/contracts';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { api, errorMessage } from '../../../lib/api';
import { useTenant, useTenantQuery } from '../../../lib/useTenant';
import { Button, ErrorText, Field } from '../../../ui';

/**
 * Le voci che il titolare aggiunge a mano a un passo progettato dal cervello (ADR 0098): programmi, procedure, ruoli. Sono
 * sue: entrano subito nel progetto, il cervello le completa e le collega al resto, e nessun riprogetto le toglie o le
 * rinomina. Si tolgono solo da qui.
 */
export function AddedItems({
  step,
  intro,
  label,
  placeholder,
  notePlaceholder,
  feminine = false,
}: {
  step: AddableStep;
  /** Una frase su cosa si aggiunge e cosa succede dopo. */
  intro: string;
  /** Il pulsante, es. «Aggiungi un programma». */
  label: string;
  placeholder: string;
  notePlaceholder: string;
  /** La voce è femminile (una procedura): «aggiunta da te», «la completo». */
  feminine?: boolean;
}) {
  const o = feminine ? 'a' : 'o';
  const lo = feminine ? 'la' : 'lo';
  const { t, tenant, isManager } = useTenant();
  const qc = useQueryClient();
  const key = ['tenant', tenant.id, 'aggiunte', step];
  const list = useTenantQuery<AddedItemDto[]>(['aggiunte', step], `/onboarding/guide/${step}/aggiunte`, {
    refetchInterval: (d) => (d?.some((x) => x.completando) ? 3000 : false),
  });
  const items = list.data ?? [];
  // Finito un completamento, il progetto del passo si rilegge: la voce compare completa e collegata.
  const busy = items.some((x) => x.completando);
  const was = useRef(busy);
  useEffect(() => {
    if (was.current && !busy) void qc.invalidateQueries({ queryKey: ['tenant', tenant.id, 'guide', step] });
    was.current = busy;
  }, [busy, qc, step, tenant.id]);

  const [open, setOpen] = useState(false);
  const [nome, setNome] = useState('');
  const [nota, setNota] = useState('');
  const done = (next: AddedItemDto[]) => {
    qc.setQueryData(key, next);
    void qc.invalidateQueries({ queryKey: ['tenant', tenant.id, 'guide', step] });
  };
  const add = useMutation({
    mutationFn: () =>
      api<AddedItemDto[]>(t(`/onboarding/guide/${step}/aggiunte`), {
        body: { nome: nome.trim(), nota: nota.trim() || null },
      }),
    onSuccess: (next) => {
      done(next);
      setNome('');
      setNota('');
      setOpen(false);
    },
  });
  const remove = useMutation({
    mutationFn: (id: string) =>
      api<AddedItemDto[]>(t(`/onboarding/guide/${step}/aggiunte/${id}`), { method: 'DELETE' }),
    onSuccess: done,
  });

  if (!isManager && !items.length) return null;
  return (
    <section className="space-y-3 rounded-2xl bg-white p-4 ring-1 ring-black/5 sm:p-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="max-w-md text-sm text-ink/60">{intro}</p>
        {isManager && !open && (
          <Button variant="ghost" className="ring-1 ring-black/10" onClick={() => setOpen(true)}>
            {label}
          </Button>
        )}
      </div>

      {!!items.length && (
        <ul className="grid gap-2">
          {items.map((x) => (
            <li
              key={x.id}
              className="flex flex-wrap items-start justify-between gap-2 rounded-xl bg-accent/[0.04] px-3 py-2 ring-1 ring-accent/15"
            >
              <div className="min-w-0">
                <p className="text-sm font-medium">
                  {x.nome} <span className="text-xs font-normal text-ink/45">· aggiunt{o} da te</span>
                </p>
                {x.nota && <p className="text-xs text-ink/55">{x.nota}</p>}
                <p className="mt-0.5 flex items-center gap-1.5 text-xs text-ink/45">
                  {x.completando ? (
                    <>
                      <span aria-hidden className="inline-block h-1.5 w-1.5 animate-pulse rounded-full bg-accent" />
                      {lo === 'la' ? 'La' : 'Lo'} completo e {lo} collego al resto del progetto…
                    </>
                  ) : x.errore ? (
                    `Non sono riuscito a completar${lo} adesso: ${lo} completo quando rifaccio il passo.`
                  ) : (
                    '✓ È nel progetto: resta così anche quando rifaccio il passo.'
                  )}
                </p>
              </div>
              {isManager && (
                <button
                  className="shrink-0 text-xs text-ink/50 hover:text-red-700 hover:underline disabled:opacity-50"
                  disabled={remove.isPending}
                  onClick={() => remove.mutate(x.id)}
                >
                  Togli
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
      <ErrorText>{remove.error ? errorMessage(remove.error) : ''}</ErrorText>

      {open && (
        <form
          className="grid gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            if (nome.trim().length >= 2 && !add.isPending) add.mutate();
          }}
        >
          <Field
            label="Nome"
            placeholder={placeholder}
            value={nome}
            onChange={(e) => setNome(e.target.value)}
            autoFocus
          />
          <Field
            label="Cosa fa, se vuoi dirlo"
            placeholder={notePlaceholder}
            value={nota}
            onChange={(e) => setNota(e.target.value)}
          />
          <div className="flex items-center gap-2">
            <Button type="submit" disabled={nome.trim().length < 2 || add.isPending}>
              {add.isPending ? 'Aggiungo…' : 'Aggiungi'}
            </Button>
            <Button type="button" variant="ghost" onClick={() => setOpen(false)}>
              Annulla
            </Button>
          </div>
          <ErrorText>{add.error ? errorMessage(add.error) : ''}</ErrorText>
        </form>
      )}
    </section>
  );
}
